import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dumbbell, Trash2 } from "lucide-react";
import { PageHeader, Stat } from "./Sugar";

const ACTIVITIES = [
  { v: "walking", l: "Walking" },
  { v: "running", l: "Running" },
  { v: "yoga", l: "Yoga" },
  { v: "gym", l: "Gym" },
  { v: "cycling", l: "Cycling" },
  { v: "swimming", l: "Swimming" },
];

export default function Exercise() {
  const [logs, setLogs] = useState([]);
  const [activity, setActivity] = useState("walking");
  const [duration, setDuration] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => { try { const { data } = await api.get("/exercise"); setLogs(data); } catch {} };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { await api.post("/exercise", { activity, duration_min: Number(duration), date }); setDuration(""); await load(); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  const del = async (id) => { await api.delete(`/exercise/${id}`); await load(); };

  const today = (() => { const s = new Date(); s.setHours(0,0,0,0); return logs.filter(l => new Date(l.logged_at || l.created_at) >= s); })();
  const todayMin = today.reduce((a, l) => a + (l.duration_min || 0), 0);
  const todayCal = today.reduce((a, l) => a + (l.calories || 0), 0);
  const weekly = (() => { const s = new Date(); s.setDate(s.getDate() - 7); return logs.filter(l => new Date(l.logged_at || l.created_at) >= s); })();

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto" data-testid="exercise-page">
      <PageHeader icon={Dumbbell} title="Exercise" subtitle="Log workouts and burn estimates" accent="amber" />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 stagger">
        <Stat label="Today" value={todayMin} unit="min" />
        <Stat label="Calories today" value={todayCal} unit="kcal" accent="rose" />
        <Stat label="This week" value={weekly.length} unit="sessions" accent="emerald" />
      </div>

      <form onSubmit={add} className="bg-white border border-slate-200 rounded-2xl p-5 mb-6 grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="exercise-form">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Date</Label>
          <Input type="date" value={date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)} className="mt-1 rounded-xl bg-slate-50" data-testid="exercise-date" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Activity</Label>
          <Select value={activity} onValueChange={setActivity}>
            <SelectTrigger className="mt-1 rounded-xl bg-slate-50" data-testid="exercise-activity"><SelectValue /></SelectTrigger>
            <SelectContent>{ACTIVITIES.map(a => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Duration (min)</Label>
          <Input type="number" required value={duration} onChange={(e) => setDuration(e.target.value)} className="mt-1 rounded-xl bg-slate-50" data-testid="exercise-duration" />
        </div>
        <div className="flex items-end">
          <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700" data-testid="exercise-submit">{loading ? "Saving…" : "Add"}</Button>
        </div>
        {error && <div className="sm:col-span-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="exercise-error">{error}</div>}
      </form>

      <h3 className="font-heading font-semibold text-slate-800 mb-3">Recent</h3>
      <div className="space-y-2" data-testid="exercise-list">
        {logs.length === 0 && <div className="text-sm text-slate-400">No sessions yet.</div>}
        {logs.map(l => (
          <div key={l.id} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-900 capitalize">{l.activity} • {l.duration_min} min</div>
              <div className="text-xs text-slate-500">{new Date(l.logged_at || l.created_at).toLocaleString()}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{l.calories} kcal</span>
              <button onClick={() => del(l.id)} data-testid={`exercise-del-${l.id}`} className="text-slate-400 hover:text-rose-500 p-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
